import React from "react";
import { motion } from "framer-motion";

const formatAppointment = (isoString) => {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-GB", options).replace(",", "");
};

const PrescriptionPreview = ({
  patientName,
  date,
  symptoms,
  diagnosis,
  medicines,
  labTests,
  sos,
  notes,
  nextAppointment,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl shadow-md p-6 border border-emerald-100 hover:shadow-xl transition-all duration-300"
    >
      <h2 className="text-2xl font-extrabold text-center text-emerald-700 mb-6">
        🧒 Child's Prescription
      </h2>

      {/* Child Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700 mb-4">
        <div>
          <p><span className="font-semibold">👤 Child Name:</span> {patientName}</p>
          <p><span className="font-semibold">📅 Date:</span> {date}</p>
        </div>
        <div>
          <p><span className="font-semibold">🚨 SOS:</span> {sos || "N/A"}</p>
        </div>
      </div>

      <hr className="my-4 border-gray-200" />

      {/* Symptoms */}
      <div className="mb-4">
        <h3 className="text-emerald-600 font-semibold mb-1">🩹 Symptoms</h3>
        <p className="text-gray-800">{symptoms || "N/A"}</p>
      </div>

      {/* Diagnosis */}
      <div className="mb-4">
        <h3 className="text-emerald-600 font-semibold mb-1">🧠 Diagnosis</h3>
        <p className="text-gray-800">{diagnosis || "N/A"}</p>
      </div>

      {/* Medicines */}
      <div className="mb-6">
        <h3 className="text-emerald-600 font-semibold mb-2">💊 Medicines</h3>
        <div className="overflow-x-auto rounded-lg">
          <table className="w-full text-sm bg-emerald-50 rounded-xl overflow-hidden">
            <thead className="bg-emerald-100 text-emerald-900">
              <tr>
                <th className="py-2 px-3 text-left">Name</th>
                <th className="py-2 px-3 text-left">Dosage</th>
                <th className="py-2 px-3 text-left">Time</th>
                <th className="py-2 px-3 text-left">Days</th>
              </tr>
            </thead>
            <tbody>
              {medicines?.length ? (
                medicines.map((med, index) => (
                  <tr key={index} className="border-t border-emerald-200">
                    <td className="py-2 px-3">{med.name || "-"}</td>
                    <td className="py-2 px-3">{med.dosage || "-"}</td>
                    <td className="py-2 px-3">{med.time || "-"}</td>
                    <td className="py-2 px-3">{med.days || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-3">
                    No medicines prescribed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lab Tests */}
      <div className="mb-6">
        <h3 className="text-emerald-600 font-semibold mb-1">🧪 Lab Tests</h3>
        <ul className="list-disc list-inside text-gray-800">
          {labTests?.filter(Boolean)?.length ? (
            labTests.map((test, index) => {
              try {
                const parsed = JSON.parse(test.replace(/'/g, '"'));
                return <li key={index}>{parsed.name}</li>;
              } catch {
                return <li key={index}>{test}</li>;
              }
            })
          ) : (
            <li>No lab tests recommended.</li>
          )}
        </ul>
      </div>

      {/* Notes */}
      {notes && (
        <div className="mb-4">
          <h3 className="text-emerald-600 font-semibold mb-1">📝 Additional Notes</h3>
          <p className="text-gray-800">{notes}</p>
        </div>
      )}

      {/* Next Appointment */}
      {nextAppointment && (
        <div className="mb-2">
          <h3 className="text-emerald-600 font-semibold mb-1">📆 Next Appointment</h3>
          <p className="text-gray-800">{formatAppointment(nextAppointment)}</p>
        </div>
      )}
    </motion.div>
  );
};

export default PrescriptionPreview;
